import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useCart } from "../contexts/CartContext";
import { useSale } from "../contexts/SaleContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FeatureProducts from "../components/FeatureProducts";
import {
  getProductBehaviorType,
  PRODUCT_BEHAVIOR,
  getDiscountedPrice,
  // getDiscountBadge,
  getEffectiveUnitPrice,
  getCartItemKey,
  getFirstEnabledUnit,
  getUnitLabel,
} from "../utils/productUtils";

const ProductDetailedView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { sale, discountLabel, categoryDiscounts } = useSale();

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState(null); // "pack" | "strip" | null

  useEffect(() => {
    document.title = `${product?.name} | dawaacare`;
  }, [product]);

  // When product loads, default selectedUnit to first enabled option
  useEffect(() => {
    if (product) {
      const behavior = getProductBehaviorType(product.type);
      if (behavior === PRODUCT_BEHAVIOR.MEDICINE) {
        setSelectedUnit(getFirstEnabledUnit(product));
      } else {
        setSelectedUnit(null);
      }
    }
  }, [product]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setQuantity(1);
    const docRef = doc(db, "products", id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) setProduct({ id: docSnap.id, ...docSnap.data() });
    }, (error) => console.error("Error fetching product detail:", error));
    return () => unsubscribe();
  }, [id]);

  if (!product) return <h2>Loading...</h2>;

  const behavior = getProductBehaviorType(product.type);
  const isMedicine = behavior === PRODUCT_BEHAVIOR.MEDICINE;
  const discounted = getDiscountedPrice(product, sale, discountLabel, selectedUnit, categoryDiscounts);
  const unitPrice = getEffectiveUnitPrice(product, sale, discountLabel, selectedUnit, categoryDiscounts);
  // const badge = getDiscountBadge(product, sale, discountLabel);
  const opts = product.sellingOptions || {};

  // Stock check for current unit
  const currentStock = isMedicine && selectedUnit
    ? (opts[selectedUnit]?.stock ?? Infinity)
    : Infinity;
  const outOfStock = product.isOutOfStock || (isMedicine && selectedUnit && currentStock <= 0);

  const handleAddToCart = () => {
    const cartKey = getCartItemKey(product.id, product.type, selectedUnit);
    const unitLabel = isMedicine ? ` (${getUnitLabel(product, selectedUnit)})` : "";
    addToCart({
      ...product,
      id: cartKey,
      productId: product.id,
      name: product.name + unitLabel,
      selectedUnit: selectedUnit || null,
      price: unitPrice,
      quantity,
    });
  };

  const handleBuyNow = () => {
    const cartKey = getCartItemKey(product.id, product.type, selectedUnit);
    const unitLabel = isMedicine ? ` (${getUnitLabel(product, selectedUnit)})` : "";
    navigate("/checkout", {
      state: {
        buyNowItem: {
          ...product,
          id: cartKey,
          productId: product.id,
          name: product.name + unitLabel,
          selectedUnit: selectedUnit || null,
          price: unitPrice,
          quantity,
        },
      },
    });
  };

  return (
    <>
      <Header />
      <div className="product-detail-container">

        {/* LEFT IMAGE */}
        <div className="product-detail-left">
          <img src={product.picture} alt={product.name} />
        </div>

        {/* RIGHT INFO */}
        <div className="product-detail-right">
          <h1>{product.name}</h1>
          <h3 className="brand">Manufactured By: {product.manufacturedBy}</h3>

          {/* Price display */}
          {isMedicine && selectedUnit ? (
            <div className="price">
              {discounted ? (
                <>
                  <span className="new-price">Rs. {discounted}</span>
                  <span className="old-price">Rs. {opts[selectedUnit]?.price}</span>
                </>
              ) : (
                <span className="new-price">Rs. {opts[selectedUnit]?.price || "—"}</span>
              )}
            </div>
          ) : (
            discounted ? (
              <div className="price">
                <span className="new-price">Rs. {discounted}</span>
                <span className="old-price">Rs. {product.retailPrice}</span>
              </div>
            ) : (
              <p className="price">Rs. {product.retailPrice || product.price}</p>
            )
          )}

          {/* Medicine-specific info */}
          {(product.formula || product.type || product.packaging) && (
            <div className="medicine-info">
              {product.formula && <p><strong>Formula:</strong> {product.formula}</p>}
              {product.type && <p><strong>Form:</strong> {product.type}</p>}
              {isMedicine && product.packaging && (
                <>
                  {product.packaging.stripsPerPack > 0 && <p><strong>Strips per Pack:</strong> {product.packaging.stripsPerPack}</p>}
                  {product.packaging.tabletsPerStrip > 0 && <p><strong>Tablets per Strip:</strong> {product.packaging.tabletsPerStrip}</p>}
                </>
              )}
              {!isMedicine && product.quantityPerPack > 0 &&
                <p><strong>Size:</strong> {product.quantityPerPack}</p>}
            </div>
          )}
          {/* ── Purchase Type Selector (medicine only) ── */}
          {isMedicine && (
            <div className="purchase-type-selector" style={{ margin: "16px 0" }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Purchase As:</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {["pack", "strip"].map((unit) => {
                  const opt = opts[unit];
                  if (!opt?.enabled) return null;
                  const isSelected = selectedUnit === unit;
                  const dPrice = getDiscountedPrice(product, sale, discountLabel, unit, categoryDiscounts);
                  const displayPrice = dPrice ?? opt.price;
                  return (
                    <button
                      key={unit}
                      onClick={() => setSelectedUnit(unit)}
                      style={{
                        padding: "10px 18px",
                        borderRadius: 8,
                        border: `2px solid ${isSelected ? "#d2222d" : "#e0e0e0"}`,
                        background: isSelected ? "#d2222d" : "#fff",
                        color: isSelected ? "#fff" : "#333",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {opt.label}
                      <span style={{ display: "block", fontSize: 12, fontWeight: 400, marginTop: 2 }}>
                        Rs. {displayPrice}
                        {opt.stock <= 0 && <span style={{ color: isSelected ? "#ffcdd2" : "#d2222d", marginLeft: 6 }}>(Out of Stock)</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {(product.description || product.usage) && (
            <>
              {/* Description */}
              < div className="description">
                <h4>Description</h4>
                <p>{product.description}</p>
              </div>

              {/* Usage */}
              <div className="usage">
                <h4>Usage Instructions</h4>
                <p>{product.usage}</p>
              </div>
            </>
          )}

          {/* Quantity stepper */}
          {!outOfStock && (
            <div className="quantity-box">
              <button onClick={() => setQuantity(q => q > 1 ? q - 1 : 1)}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
          )}

          {/* Add to Cart / Buy Now */}
          {outOfStock ? (
            <div className="product-oos-banner">
              <span className="product-oos-icon">⚠</span>
              <span>This product is currently <strong>Out of Stock</strong>. Please check back later.</span>
            </div>
          ) : (
            <div className="detail-action-row">
              <button className="buy-now-btn" onClick={handleBuyNow}>Buy Now</button>
              <button className="add-to-cart-btn" onClick={handleAddToCart}>Add to Cart</button>
            </div>
          )}
        </div>
      </div >

      <FeatureProducts />
      <Footer />
    </>
  );
};

export default ProductDetailedView;